import React from 'react';
import ResourceLink from './resource-link';
import { isSafeInternalPath } from '../model/route-action';

/**
 * Small Markdown renderer for copilot replies: paragraphs, headings, bullet/numbered lists,
 * tables, fenced code, inline code, bold, italic and http(s) links. Builds React elements only
 * (no HTML injection), since replies can quote builder- and customer-supplied text.
 */

const INLINE = /(`[^`]+`|\*\*[^*]+\*\*|\*[^*\s][^*]*\*|\[[^\]]+\]\(((?:https?:\/\/|\/)[^)\s]+)\))/g;

export function renderInline(text, keyPrefix = 'i') {
    const parts = [];
    let last = 0;
    let match;
    let n = 0;
    INLINE.lastIndex = 0;
    while ((match = INLINE.exec(text)) !== null) {
        if (match.index > last) parts.push(text.slice(last, match.index));
        const token = match[0];
        const key = `${keyPrefix}-${n++}`;
        if (token.startsWith('`')) {
            parts.push(<code key={key} className="rounded bg-gray-100 px-1 py-0.5 font-mono text-[0.85em] text-gray-800">{token.slice(1, -1)}</code>);
        } else if (token.startsWith('**')) {
            parts.push(<strong key={key} className="font-semibold">{token.slice(2, -2)}</strong>);
        } else if (token.startsWith('[')) {
            const label = token.slice(1, token.indexOf(']'));
            const className = "font-medium underline underline-offset-2 hover:text-gray-600";
            if (isSafeInternalPath(match[2])) {
                parts.push(<ResourceLink key={key} to={match[2]} className={className}>{label}</ResourceLink>);
            } else if (/^https?:\/\//.test(match[2])) {
                parts.push(<a key={key} href={match[2]} target="_blank" rel="noreferrer" className={className}>{label}</a>);
            } else {
                parts.push(label);
            }
        } else {
            parts.push(<em key={key}>{token.slice(1, -1)}</em>);
        }
        last = match.index + token.length;
    }
    if (last < text.length) parts.push(text.slice(last));
    return parts;
}

// Split only unescaped pipes, preserving literal pipes inside cell content.
function splitTableRow(line) {
    const cells = [''];
    for (const char of line.trim()) {
        const last = cells.length - 1;
        const slashes = cells[last].match(/\\+$/)?.[0].length || 0;
        if (char === '|' && slashes % 2 === 0) {
            cells.push('');
        } else if (char === '|') {
            cells[last] = cells[last].slice(0, -1) + char;
        } else {
            cells[last] += char;
        }
    }
    if (cells.length === 1) return null;
    if (!cells[0].trim()) cells.shift();
    if (cells.length && !cells[cells.length - 1].trim()) cells.pop();
    return cells.map(cell => cell.trim());
}

function tableHeader(lines, index) {
    if (index + 1 >= lines.length) return null;
    const headers = splitTableRow(lines[index]);
    const separators = splitTableRow(lines[index + 1]);
    if (!headers?.length || headers.length !== separators?.length ||
        !separators.every(cell => /^:?-{3,}:?$/.test(cell))) return null;
    return {
        type: 'table',
        headers,
        alignments: separators.map(cell => cell.endsWith(':') ? (cell.startsWith(':') ? 'center' : 'right') : 'left'),
        rows: [],
    };
}

export function parseBlocks(source) {
    const lines = String(source || '').replace(/\r\n/g, '\n').split('\n');
    const blocks = [];
    let i = 0;
    while (i < lines.length) {
        const line = lines[i];
        const table = tableHeader(lines, i);
        if (/^```/.test(line)) {
            const code = [];
            i += 1;
            while (i < lines.length && !/^```/.test(lines[i])) code.push(lines[i++]);
            i += 1;
            blocks.push({ type: 'code', text: code.join('\n') });
        } else if (table) {
            i += 2;
            while (i < lines.length && lines[i].trim() && !tableHeader(lines, i) && !/^(```|\s*[-*]\s+|\s*\d+[.)]\s+|#{1,6}\s+)/.test(lines[i])) {
                const cells = splitTableRow(lines[i]);
                if (!cells) break;
                table.rows.push(table.headers.map((_, column) => cells[column] || ''));
                i += 1;
            }
            blocks.push(table);
        } else if (/^\s*[-*]\s+/.test(line)) {
            const items = [];
            while (i < lines.length && /^\s*[-*]\s+/.test(lines[i])) items.push(lines[i++].replace(/^\s*[-*]\s+/, ''));
            blocks.push({ type: 'ul', items });
        } else if (/^\s*\d+[.)]\s+/.test(line)) {
            const items = [];
            while (i < lines.length && /^\s*\d+[.)]\s+/.test(lines[i])) items.push(lines[i++].replace(/^\s*\d+[.)]\s+/, ''));
            blocks.push({ type: 'ol', items });
        } else if (/^#{1,6}\s+/.test(line)) {
            blocks.push({ type: 'heading', text: line.replace(/^#{1,6}\s+/, '') });
            i += 1;
        } else if (!line.trim()) {
            i += 1;
        } else {
            const text = [];
            while (i < lines.length && lines[i].trim() && !tableHeader(lines, i) && !/^(```|\s*[-*]\s+|\s*\d+[.)]\s+|#{1,6}\s+)/.test(lines[i])) {
                text.push(lines[i++]);
            }
            blocks.push({ type: 'p', text: text.join('\n') });
        }
    }
    return blocks;
}

export default function Markdown({ text }) {
    if (typeof text !== 'string') text = text == null ? '' : String(text);
    return (
        <div className="min-w-0 max-w-full space-y-2 break-words text-[0.925rem] leading-relaxed">
            {parseBlocks(text).map((block, index) => {
                const key = `b${index}`;
                switch (block.type) {
                    case 'code':
                        return <pre key={key} className="overflow-x-auto rounded-xl bg-gray-900 p-3 font-mono text-xs leading-relaxed text-gray-100 dark:bg-black/60">{block.text}</pre>;
                    case 'table':
                        return (
                            <div key={key} className="max-w-full overflow-x-auto rounded-lg border border-gray-200 bg-white dark:bg-transparent">
                                <table className="w-full border-collapse text-sm">
                                    <thead className="bg-gray-50">
                                        <tr>{block.headers.map((cell, column) => (
                                            <th key={column} scope="col" style={{ textAlign: block.alignments[column] }} className="min-w-[8rem] border-b border-gray-200 px-3 py-2 align-top font-semibold">
                                                {renderInline(cell, `${key}-h-${column}`)}
                                            </th>
                                        ))}</tr>
                                    </thead>
                                    <tbody>{block.rows.map((row, rowIndex) => (
                                        <tr key={rowIndex} className="border-b border-gray-200 last:border-b-0">
                                            {row.map((cell, column) => (
                                                <td key={column} style={{ textAlign: block.alignments[column] }} className="px-3 py-2 align-top [overflow-wrap:anywhere]">
                                                    {renderInline(cell, `${key}-${rowIndex}-${column}`)}
                                                </td>
                                            ))}
                                        </tr>
                                    ))}</tbody>
                                </table>
                            </div>
                        );
                    case 'ul':
                        return <ul key={key} className="list-disc space-y-1 pl-5">{block.items.map((item, j) => <li key={j}>{renderInline(item, `${key}-${j}`)}</li>)}</ul>;
                    case 'ol':
                        return <ol key={key} className="list-decimal space-y-1 pl-5">{block.items.map((item, j) => <li key={j}>{renderInline(item, `${key}-${j}`)}</li>)}</ol>;
                    case 'heading':
                        return <p key={key} className="font-semibold">{renderInline(block.text, key)}</p>;
                    default:
                        return <p key={key} className="whitespace-pre-line">{renderInline(block.text, key)}</p>;
                }
            })}
        </div>
    );
}
